import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import { T } from "@/components/T";

const PARTICLES = [
  { left: "5%",  bottom: "15%", size: 2,   delay: 0,   dur: 9 },
  { left: "11%", bottom: "48%", size: 1.5, delay: 1.2, dur: 7.5 },
  { left: "19%", bottom: "8%",  size: 2.5, delay: 2.4, dur: 8.5 },
  { left: "28%", bottom: "72%", size: 1,   delay: 0.8, dur: 11 },
  { left: "36%", bottom: "25%", size: 2,   delay: 3.5, dur: 7 },
  { left: "44%", bottom: "88%", size: 1.5, delay: 1.6, dur: 9.5 },
  { left: "52%", bottom: "35%", size: 1,   delay: 4.2, dur: 8 },
  { left: "60%", bottom: "60%", size: 2.5, delay: 0.5, dur: 10 },
  { left: "68%", bottom: "18%", size: 1.5, delay: 2.8, dur: 7.5 },
  { left: "76%", bottom: "78%", size: 2,   delay: 1.9, dur: 9 },
  { left: "83%", bottom: "42%", size: 1,   delay: 3.1, dur: 8.5 },
  { left: "90%", bottom: "10%", size: 2,   delay: 5.3, dur: 6.5 },
  { left: "23%", bottom: "55%", size: 1.5, delay: 4.7, dur: 10 },
  { left: "48%", bottom: "92%", size: 1,   delay: 2.1, dur: 8 },
  { left: "73%", bottom: "65%", size: 2.5, delay: 6.2, dur: 7.5 },
];

export function HeroSection() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden grain-overlay"
      style={{ background: "#0b0c0e" }}
    >
      <div className="absolute pointer-events-none select-none z-0"
        style={{ top: "50%", left: "22%", transform: "translate(-50%, -50%)", width: "700px", height: "700px",
          background: "radial-gradient(ellipse, rgba(245,200,0,0.055) 0%, transparent 65%)",
          animation: "breathe 5s ease-in-out infinite" }} />

      {PARTICLES.map((p, i) => (
        <div key={i} className="sawdust-particle"
          style={{ left: p.left, bottom: p.bottom, width: `${p.size}px`, height: `${p.size}px`,
            animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s` }} />
      ))}

      <div className="absolute right-0 top-0 bottom-0 w-[52%] z-0 hidden lg:block">
        <img src="/assets/fotos/hb-foto-1-2.jpeg" alt="Premium Holzbau Südtirol – Holzbuddy"
          className="w-full h-full object-cover" style={{ objectPosition: "center 40%" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b0c0e] via-[#0b0c0e]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c0e]/30 to-transparent" />
      </div>

      <div className="relative z-10 container-hb w-full pt-24 pb-20">
        <div className="max-w-[560px]">
          <img src="/logo-white-yellow.png" alt="Holzbuddy.team" className="h-12 md:h-14 mb-10 logo-glow" />

          <div className="flex items-center gap-2 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" style={{ animation: "pulse 2s ease-in-out infinite" }} />
            <span className="text-[9px] tracking-[0.24em] uppercase font-inter" style={{ color: "rgba(255,255,255,0.35)" }}>
              <T de={`Neue Projekte ${new Date().getFullYear()} · Termine noch verfügbar`} it={`Nuovi progetti ${new Date().getFullYear()} · Posti ancora disponibili`} />
            </span>
          </div>

          <div className="flex items-center gap-3 mb-8">
            <span className="block w-7 h-px bg-hb-yellow/50" />
            <span className="text-[10px] tracking-[0.28em] uppercase text-white/35 font-inter">
              <T de="Meran · Südtirol · Holzbau" it="Merano · Alto Adige · Costruzione in legno" />
            </span>
          </div>

          <h1 data-testid="hero-headline" className="font-bebas text-white mb-8"
            style={{ fontSize: "clamp(68px, 9vw, 118px)", lineHeight: 0.95, letterSpacing: "0.01em" }}>
            <T de={<>Präzise<br />Geplant.<br /><span className="text-hb-yellow">Sauber</span><br /><span className="text-hb-yellow">Umgesetzt.</span></>}
               it={<>Precisi.<br />Pianificati.<br /><span className="text-hb-yellow">Eseguiti</span><br /><span className="text-hb-yellow">con cura.</span></>} />
          </h1>

          <p className="text-sm text-white/50 font-inter leading-relaxed mb-10 max-w-[300px]">
            <T de="Das Holzbuddy Team realisiert Dachstühle, Aufstockungen, Holzfassaden und anspruchsvolle Holzbauprojekte in ganz Südtirol."
               it="Il team Holzbuddy realizza strutture del tetto, sopraelevazioni, facciate in legno e costruzioni in legno in tutto l'Alto Adige." />
          </p>

          <div className="flex flex-wrap gap-3 mb-14">
            <Link to="/anfrage" data-testid="hero-primary-cta" className="btn-primary">
              <T de="Projekt anfragen" it="Richiedi un progetto" />
            </Link>
            <Link to="/projekte" data-testid="hero-secondary-cta"
              className="border border-white/20 text-white hover:border-hb-yellow hover:text-hb-yellow text-xs tracking-widest uppercase font-semibold px-8 py-4 transition-all duration-300 font-inter">
              <T de="Referenzen" it="Riferimenti" />
            </Link>
            <a href="tel:+390473238030" data-testid="hero-callback-cta"
              className="hidden sm:flex items-center gap-2 text-xs text-white/30 hover:text-hb-yellow transition-colors font-inter self-center pl-2">
              <Phone size={11} /> <T de="Anrufen" it="Chiama" />
            </a>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-px h-10 bg-white/12 relative overflow-hidden">
              <div className="absolute left-0 w-full bg-hb-yellow"
                style={{ height: "45%", animation: "scrollDown 2.2s ease-in-out infinite" }} />
            </div>
            <span className="text-[9px] tracking-widest uppercase text-white/18 font-inter">
              <T de="Scrollen" it="Scorri" />
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
